// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/advancedSpeech.js
//  ALTO RENDIMIENTO — voz con acento regional
//
//  ── POR QUÉ UNA COPIA Y NO `utils/browserSpeech` ────────────────────────────
//  Ese módulo fija una sola voz en inglés británico y la cachea, porque eso es
//  lo que necesitan el examen, el Dojo y la sala de entrevistas. Aquí hace falta
//  lo contrario: elegir voz POR ACENTO en cada reactivo y hablar más rápido de
//  lo normal. Tocar el módulo compartido para esto obligaría a revisar tres
//  microservicios en producción; copiarlo cuesta setenta líneas.
//
//  Es el mismo precedente que sentó `components/onboarding/onboardingSpeech.js`.
//
//  ── LO QUE ESTO ES Y LO QUE NO ES ───────────────────────────────────────────
//  Son voces sintéticas del sistema operativo con variante regional real
//  (en-GB, en-US, en-AU, en-IN), aceleradas a ritmo de conversación. NO es
//  audio de hablante humano grabado.
//
//  Es un peldaño intermedio honesto entre el dictado lento de las apps y una
//  llamada real, y la pantalla lo dice con estas palabras. Cuántas voces haya
//  disponibles depende del sistema del alumno, así que `availableAccents()`
//  informa de lo que hay de verdad en ESE dispositivo en vez de prometer cuatro.
// ═══════════════════════════════════════════════════════════════════════════════

// `label` es el nombre de siempre, en español, y no se toca: lo usan Oído
// nativo y los demás formatos de la sala, que siguen en español.
//
// `label_en` existe porque La Situación sí cambia de idioma, y una pantalla
// entera en inglés con un botón que dice «Británico» delata que la traducción
// llegó hasta la mitad. Quien no lo necesite sigue leyendo `label` y no se
// entera de que esto está aquí.
export const ACCENTS = {
  'en-GB': { flag: '🇬🇧', label: 'Británico', label_en: 'British' },
  'en-US': { flag: '🇺🇸', label: 'Americano', label_en: 'American' },
  'en-AU': { flag: '🇦🇺', label: 'Australiano', label_en: 'Australian' },
  'en-IN': { flag: '🇮🇳', label: 'Indio', label_en: 'Indian' },
};

let cache = null;

function voices() {
  return window.speechSynthesis?.getVoices() ?? [];
}

if (typeof window !== 'undefined' && window.speechSynthesis) {
  // Chrome carga las voces de forma asíncrona.
  window.speechSynthesis.onvoiceschanged = () => { cache = null; };
  window.speechSynthesis.getVoices();
}

export function isAvailable() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/** Mapa acento → voz real del dispositivo. Se calcula una vez. */
function voiceMap() {
  if (cache) return cache;
  const all = voices();
  cache = {};
  Object.keys(ACCENTS).forEach((lang) => {
    cache[lang] = all.find((v) => v.lang === lang)
      || all.find((v) => v.lang?.replace('_', '-') === lang)
      || null;
  });
  return cache;
}

/**
 * Qué acentos existen DE VERDAD en este dispositivo.
 * La pantalla lo usa para no anunciar cuatro cuando el sistema sólo tiene dos.
 */
export function availableAccents() {
  if (!isAvailable()) return [];
  const map = voiceMap();
  return Object.keys(ACCENTS).filter((lang) => map[lang]);
}

/**
 * Reproduce una línea con el acento pedido, a la velocidad pedida.
 * Si el acento no existe en el dispositivo, cae a cualquier voz inglesa y lo
 * avisa por `onFallback` — nunca se finge un acento que no se está usando.
 */
export function say(text, { accent = 'en-US', rate = 1.2 } = {},
                    { onStart, onEnd, onError, onFallback } = {}) {
  if (!isAvailable() || !text) {
    onError?.('speech-unavailable');
    return;
  }

  window.speechSynthesis.cancel();

  const utt = new SpeechSynthesisUtterance(text);
  const picked = voiceMap()[accent];

  if (picked) {
    utt.voice = picked;
    utt.lang = picked.lang;
  } else {
    const any = voices().find((v) => v.lang?.startsWith('en'));
    if (any) { utt.voice = any; utt.lang = any.lang; }
    else utt.lang = 'en-US';
    onFallback?.(accent);
  }

  // Tope de seguridad: por encima de 1.6 el sintetizador deja de articular y
  // el ejercicio se vuelve imposible en vez de difícil.
  utt.rate = Math.max(0.6, Math.min(1.6, rate));
  utt.pitch = 1.0;
  utt.volume = 1.0;

  utt.onstart = () => onStart?.();
  utt.onend = () => onEnd?.();
  utt.onerror = (e) => onError?.(e?.error ?? 'speech-error');

  // Chrome recolecta la utterance si nadie la referencia.
  window._nslAdvancedUtt = utt;
  window.speechSynthesis.speak(utt);
}

export function stop() {
  if (isAvailable()) window.speechSynthesis.cancel();
}
